import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { CheckCircle, XCircle, Zap, Minus, Award, AlertTriangle, ArrowLeft, Clock, Printer, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import EvaluationRequestPanel from '@/components/EvaluationRequestPanel';

const STATUS_CONFIG = {
  posee: { icon: CheckCircle, color: '#24B219', bg: '#f0fdf4', border: '#bbf7d0', label: 'Posée' },
  non_posee: { icon: XCircle, color: '#EE2130', bg: '#fef2f2', border: '#fecaca', label: 'Non posée' },
  repondue_spontanement: { icon: Zap, color: '#F4621C', bg: '#fff7ed', border: '#fed7aa', label: 'Répondue spontanément' },
  non_applicable: { icon: Minus, color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb', label: 'Non applicable' },
};

const QuestionRow = ({ item, isAdvanced }) => {
  const config = STATUS_CONFIG[item.statut] || STATUS_CONFIG.non_applicable;
  const Icon = config.icon;

  return (
    <div
      className="flex items-start gap-3 p-3 rounded-lg border transition-all"
      style={{ backgroundColor: config.bg, borderColor: config.border }}
      data-testid={`retroaction-item-${item.id}`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: config.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-800">
            {isAdvanced ? item.question : getQuestionLabel(item.id)}
          </span>
          <Badge
            variant="outline"
            className="text-xs text-white"
            style={{ backgroundColor: config.color }}
          >
            {config.label}
          </Badge>
        </div>
        {item.commentaire && (
          <p className="text-xs text-gray-600 mt-1">{item.commentaire}</p>
        )}
      </div>
    </div>
  );
};

const PRIMARY_LABELS = {
  adresse: "C'est pour quelle adresse?",
  intersection: "À quelle intersection?",
  quoi: "Qu'est-ce qui se passe? (Question ouverte)",
};

function getQuestionLabel(id) {
  return PRIMARY_LABELS[id] || id;
}

const ScoreGauge = ({ score }) => {
  const getColor = (s) => {
    if (s >= 80) return '#24B219';
    if (s >= 60) return '#F4621C';
    return '#EE2130';
  };
  const color = getColor(score);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-32 h-32 mx-auto" data-testid="score-gauge">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="50" cy="50" r="45" fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>{score}</span>
        <span className="text-xs text-gray-500">/ 100</span>
      </div>
    </div>
  );
};

const Retroaction = ({ studentInfo, isAdmin }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [retroaction, setRetroaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadRetroaction();
  }, [sessionId]);

  const loadRetroaction = async () => {
    setLoading(true);
    try {
      // Try to get existing retroaction
      const resp = await axios.get(`${API}/sessions/${sessionId}/retroaction`);
      setRetroaction(resp.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // Génération réservée à l'admin — l'étudiant fait une demande
        setNotFound(true);
      } else {
        toast.error("Erreur lors du chargement de la rétroaction");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateRetroaction = async () => {
    setGenerating(true);
    try {
      const resp = await axios.post(`${API}/sessions/${sessionId}/retroaction?admin=true`);
      setRetroaction(resp.data);
      setNotFound(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading || generating) {
    return (
      <div data-testid="retroaction-loading" className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: '#2E6CF5', borderTopColor: 'transparent' }} />
          <Target className="w-8 h-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ color: '#002768' }} />
        </div>
        <p className="text-lg font-semibold" style={{ color: '#002768' }}>
          {generating ? "Analyse de votre performance en cours..." : "Chargement de la rétroaction..."}
        </p>
        <p className="text-sm text-gray-500">L'IA analyse votre questionnement selon le référentiel incendie</p>
      </div>
    );
  }

  if (notFound && !retroaction) {
    return (
      <div className="min-h-screen pt-24 px-4">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => navigate('/history')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium mb-4"
            data-testid="back-to-history"
          >
            <ArrowLeft className="w-4 h-4" />
            Historique
          </button>
          <div className="text-center mb-5">
            <Target className="w-10 h-10 mx-auto mb-2" style={{ color: '#002768' }} />
            <h1 className="text-xl font-bold" style={{ color: '#002768' }}>Rétroaction Incendie</h1>
            <p className="text-sm text-gray-600">Aucune rétroaction n'existe encore pour cette session.</p>
          </div>
          <EvaluationRequestPanel
            requestType="retroaction"
            sessionType="training"
            sessionId={sessionId}
            studentInfo={studentInfo}
            isAdmin={isAdmin}
            onAdminGenerate={generateRetroaction}
            generating={generating}
          />
        </div>
      </div>
    );
  }

  if (!retroaction) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertTriangle className="w-12 h-12 text-amber-500" />
        <p className="text-lg text-gray-700">Aucune rétroaction disponible</p>
        <button onClick={() => navigate('/history')} className="px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#002768' }}>
          Retour à l'historique
        </button>
      </div>
    );
  }

  const data = retroaction.data || {};
  const primaire = data.primaire || [];
  const avance = data.avance || [];
  const score = data.score_global || 0;

  const countByStatus = (items) => {
    const counts = { posee: 0, non_posee: 0, repondue_spontanement: 0, non_applicable: 0 };
    items.forEach(item => { counts[item.statut] = (counts[item.statut] || 0) + 1; });
    return counts;
  };

  const primaireCounts = countByStatus(primaire);
  const avanceCounts = countByStatus(avance.filter(q => q.statut !== 'non_applicable'));

  return (
    <div data-testid="retroaction-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-5xl mx-auto print:pt-4 print:px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button
          onClick={() => navigate('/history')}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          data-testid="back-to-history"
        >
          <ArrowLeft className="w-4 h-4" />
          Historique
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
          style={{ backgroundColor: '#002768' }}
          data-testid="print-retroaction"
        >
          <Printer className="w-4 h-4" />
          Imprimer
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: '#002768' }}>
          Rétroaction
        </h1>
        <p className="text-gray-600 mt-1">
          {retroaction.scenario_title} — {retroaction.operator_name}
        </p>
        {retroaction.created_at && (
          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" />
            {new Date(retroaction.created_at).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      {/* Score + Nature */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="glass border-0">
          <CardContent className="pt-6 pb-4 text-center">
            <ScoreGauge score={score} />
            <p className="text-sm font-medium text-gray-600 mt-2">Score global</p>
          </CardContent>
        </Card>
        <Card className="glass border-0">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-gray-500 mb-2">Nature identifiée</p>
            <p className="text-xl font-bold" style={{ color: '#002768' }}>{data.nature_identifiee || 'N/A'}</p>
            {data.categories_pertinentes?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {data.categories_pertinentes.map(cat => (
                  <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="glass border-0">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-gray-500 mb-3">Résumé</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#24B219' }} />
                <span>{primaireCounts.posee + avanceCounts.posee} question(s) posée(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4" style={{ color: '#EE2130' }} />
                <span>{primaireCounts.non_posee + avanceCounts.non_posee} question(s) manquée(s)</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" style={{ color: '#F4621C' }} />
                <span>{primaireCounts.repondue_spontanement + avanceCounts.repondue_spontanement} répondue(s) spontanément</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Questionnement Primaire */}
      <Card className="glass border-0 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#002768' }}>
            <Target className="w-5 h-5" />
            Questionnement Primaire
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {primaire.map(item => (
            <QuestionRow key={item.id} item={item} isAdvanced={false} />
          ))}
          {primaire.length === 0 && (
            <p className="text-gray-500 text-sm py-4 text-center">Aucune donnée primaire disponible</p>
          )}
        </CardContent>
      </Card>

      {/* Questionnement Avancé */}
      <Card className="glass border-0 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#002768' }}>
            <Award className="w-5 h-5" />
            Questionnement Avancé — {data.nature_identifiee || 'N/A'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {avance.map((item, idx) => (
            <QuestionRow key={item.id || idx} item={item} isAdvanced={true} />
          ))}
          {avance.length === 0 && (
            <p className="text-gray-500 text-sm py-4 text-center">Aucune donnée avancée disponible</p>
          )}
        </CardContent>
      </Card>

      {/* Points forts & Améliorations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="border-2" style={{ borderColor: '#24B219', backgroundColor: '#f0fdf4' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: '#24B219' }}>
              <CheckCircle className="w-5 h-5" />
              Points forts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              {(data.points_forts || []).map((p, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#24B219' }} />
                  {p}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card className="border-2" style={{ borderColor: '#F4621C', backgroundColor: '#fff7ed' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: '#F4621C' }}>
              <AlertTriangle className="w-5 h-5" />
              Points à améliorer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              {(data.points_ameliorer || []).map((p, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#F4621C' }} />
                  {p}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Commentaire général */}
      {data.commentaire_general && (
        <Card className="glass border-0 mb-6">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-700 leading-relaxed">{data.commentaire_general}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Retroaction;
